import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { login } from "../api/apis";
import OtpEnter from "../Pages/OtpEnter";
import { ButtonLoader } from '../Components/LoadingStates';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useAuth } from "../context/AuthContext";
import logo from '../assets/Images/logo.png';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    emailId: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [otpMenuToggle, setOtpMenuToggle] = useState(false);
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin } = useAuth();
  
  // Preserve full target (object with pathname/search/hash or a string)
  const from = location.state?.from || '/dashboard';
  const searchParams = new URLSearchParams(location.search);
  const nextParam = searchParams.get('next');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.emailId) {
      newErrors.emailId = "Email ID is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId)) {
      newErrors.emailId = "Invalid email ID format.";
    }
    if (!formData.password) {
      newErrors.password = "Password is required.";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length === 0) {
      try {
        setLoader(true);
        const response = await login(formData);
        console.log('[LoginPage] HTTP status:', response?.status);
        console.log('[LoginPage] Response data:', response?.data);

        if (response?.data?.token) {
          // If we have a token, login was successful
          authLogin(response.data.user, response.data.token);
          // Mark recent login to suppress transient auth toasts
          window.__justLoggedIn = Date.now();
          toast.success("Login successful!");
          // Prefer explicit post-login redirect captured by guards or pages
          let target = from;
          try {
            const ss = sessionStorage.getItem('postLoginRedirect');
            if (ss && typeof ss === 'string') {
              target = ss;
              sessionStorage.removeItem('postLoginRedirect');
            }
          } catch {}
          if (!target && nextParam) {
            target = nextParam;
          }
          navigate(target, { replace: true });
        } else if (response?.data?.requiresOTP) {
          // If OTP verification is required
          toast.info(response.data.message || "Please verify your email with OTP");
          setOtpMenuToggle(true);
        } else {
          // Handle other error cases
          const errorMessage = response.data?.error || "Login failed";
          toast.error(errorMessage);
        }
      } catch (error) {
        console.error('Login error:', error);
        // Check if this is an Axios error with response data
        if (error.response) {
          // Handle 202 status (OTP required)
          if (error.response.status === 202 && error.response.data?.requiresOTP) {
            toast.info(error.response.data.message || "Please verify your email with OTP");
            setOtpMenuToggle(true);
            return;
          }
          
          const errorMessage = error.response.data?.error || 
                             error.response.data?.message || 
                             "Login failed";
          toast.error(errorMessage);
        } else if (error.request) {
          // The request was made but no response was received
          console.error('No response received:', error.request);
          toast.error("No response from server. Please try again later.");
        } else {
          // Something happened in setting up the request
          console.error('Request setup error:', error.message);
          toast.error(error.message || "An error occurred. Please try again.");
        }
      } finally {
        setLoader(false);
      }
    }
  };

  return (
    otpMenuToggle ? (
      <OtpEnter
        setOtpMenuToggle={setOtpMenuToggle}
        isLogin={true}
        emailId={formData.emailId}
        password={formData.password}
        showPasswordField={false}
      />
    ) : (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white overflow-x-hidden">
        {/* Futuristic Background Effects */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-conic from-yellow-500/10 via-transparent to-yellow-500/10 rounded-full blur-3xl animate-spin" style={{animationDuration: '20s'}}></div>
        </div>

        {/* Local header removed: using global Layout header */}

        {/* Main Content (reduced top padding, global fixed header already present) */}
        <main className="pt-6 pb-12 px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-md mx-auto">
            <div className="bg-black/30 rounded-2xl p-8 backdrop-blur-sm border border-yellow-500/20 shadow-2xl">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-yellow-600/20 bg-black/20">
                  <img
                    src={logo}
                    alt="Crezar Logo"
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent mb-2">
                  Welcome Back
                </h1>
                <p className="text-gray-400">Sign in to access your gaming dashboard</p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Input */}
                <div className="space-y-2">
                  <label htmlFor="emailId" className="block text-sm font-medium text-gray-300">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-yellow-400/80 w-5 h-5 z-10 pointer-events-none" />
                    <input
                      id="emailId"
                      type="email"
                      name="emailId"
                      value={formData.emailId}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-4 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-400 focus:outline-none transition-colors backdrop-blur-sm ${
                        errors.emailId ? 'border-red-500 focus:border-red-500' : 'border-gray-700 focus:border-yellow-500'
                      }`}
                      placeholder="Enter your email"
                    />
                  </div>
                  {errors.emailId && <p className="text-red-400 text-sm">{errors.emailId}</p>}
                </div>

                {/* Password Input */}
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                    Password
                  </label>
                  <div className="relative">
                    <Lock aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-yellow-400/80 w-5 h-5 z-10 pointer-events-none" />
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-12 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-400 focus:outline-none transition-colors backdrop-blur-sm ${
                        errors.password ? 'border-red-500 focus:border-red-500' : 'border-gray-700 focus:border-yellow-500'
                      }`}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-400 text-sm">{errors.password}</p>}
                </div>

                {/* Forgot Password */}
                <div className="flex justify-end">
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-yellow-400 hover:text-yellow-300 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Submit Button */}
                <ButtonLoader
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg shadow-yellow-500/25"
                  loading={loader}
                  disabled={loader}
                >
                  <span>Sign In</span>
                  <ArrowRight className="w-4 h-4" />
                </ButtonLoader>
              </form>

              {/* Footer */}
              <div className="mt-8 text-center">
                <p className="text-gray-400 text-sm">
                  Don't have an account?{" "}
                  <button
                    type="button"
                    onClick={() => navigate({ pathname: '/signup', search: location.search }, { state: { from: location.state?.from || { pathname: '/dashboard' } } })}
                    className="text-yellow-400 hover:text-yellow-300 transition-colors font-semibold underline"
                  >
                    Create Account
                  </button>
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  );
};

export default LoginPage;
